import React from 'react';
import { Target, Eye, Compass, Award } from 'lucide-react';
import { useLanguage } from '../i18n';
import { useSiteSettings } from '../hooks/useSiteSettings';
import { EditableText } from '../components/InlineEdit';

const MissionPage = () => {
  const { t, language } = useLanguage();
  const { settings } = useSiteSettings();

  return (
    <div className="min-h-screen bg-[#F9F8F6] pt-24" data-testid="mission-page">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center max-w-3xl mx-auto animate-fade-in-up">
            <span className="text-sm uppercase tracking-widest text-[#BFA05F] font-medium">
              <EditableText settingKey="brand_name" defaultValue="Lou's Way" />
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight text-[#1A1815] mt-4">
              <EditableText 
                settingKey={`mission_title_${language}`} 
                defaultValue={t('mission.title')} 
              />
            </h1>
            <p className="text-lg text-[#635E54] mt-6 leading-relaxed">
              <EditableText 
                settingKey={`mission_subtitle_${language}`} 
                defaultValue={t('mission.subtitle')} 
                multiline
              />
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Mission */}
            <div
              className="bg-white p-10 md:p-12 rounded-3xl border border-[#E6E2DA] animate-fade-in-up"
              data-testid="mission-card"
            >
              <div className="w-16 h-16 rounded-2xl bg-[#1A1815] flex items-center justify-center mb-8">
                <Target className="w-8 h-8 text-[#BFA05F]" />
              </div>
              <h2 className="font-heading text-3xl font-medium text-[#1A1815] mb-6">
                {language === 'pt' ? 'Missão' : 'Mission'}
              </h2>
              <p className="text-[#635E54] leading-relaxed text-lg">
                <EditableText 
                  settingKey={`mission_text_${language}`} 
                  defaultValue={t('mission.mission_text')} 
                  multiline
                />
              </p>
            </div>

            {/* Vision */}
            <div
              className="bg-[#1A1815] p-10 md:p-12 rounded-3xl text-white animate-fade-in-up animation-delay-100"
              data-testid="vision-card"
            >
              <div className="w-16 h-16 rounded-2xl bg-[#BFA05F] flex items-center justify-center mb-8">
                <Eye className="w-8 h-8 text-[#1A1815]" />
              </div>
              <h2 className="font-heading text-3xl font-medium mb-6">
                <EditableText 
                  settingKey={`vision_title_${language}`} 
                  defaultValue={language === 'pt' ? 'Visão' : 'Vision'} 
                />
              </h2>
              <p className="text-[#A8A29A] leading-relaxed text-lg">
                <EditableText 
                  settingKey={`vision_text_${language}`} 
                  defaultValue={t('mission.vision_text')} 
                  multiline
                  className="text-[#A8A29A]"
                />
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-16 animate-fade-in-up">
            <span className="text-sm uppercase tracking-widest text-[#BFA05F] font-medium">
              {language === 'pt' ? 'Nossos Valores' : 'Our Values'}
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-normal text-[#1A1815] mt-4">
              {language === 'pt' ? 'Princípios que nos ' : 'Principles that '}
              <span className="italic text-[#BFA05F]">{language === 'pt' ? 'guiam' : 'guide us'}</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Compass,
                titleKey: 'value_1_title',
                descKey: 'value_1_desc',
                defaultTitle: language === 'pt' ? 'Integridade' : 'Integrity',
                defaultDesc: language === 'pt'
                  ? 'Transparência e honestidade em todas as interações.'
                  : 'Transparency and honesty in all interactions.',
              },
              {
                icon: Award,
                titleKey: 'value_2_title',
                descKey: 'value_2_desc',
                defaultTitle: language === 'pt' ? 'Qualidade' : 'Quality',
                defaultDesc: language === 'pt'
                  ? 'Compromisso com os mais altos padrões de excelência.'
                  : 'Commitment to the highest standards of excellence.',
              },
              {
                icon: Target,
                titleKey: 'value_3_title',
                descKey: 'value_3_desc',
                defaultTitle: language === 'pt' ? 'Foco no Cliente' : 'Client Focus',
                defaultDesc: language === 'pt'
                  ? 'Cada decisão é tomada pensando no seu bem-estar.'
                  : 'Every decision is made with your well-being in mind.',
              },
            ].map((item, index) => (
              <div
                key={index}
                className="text-center p-8 rounded-2xl bg-[#F9F8F6] hover:bg-[#F2F0EB] transition-colors animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`value-item-${index}`}
              >
                <div className="w-14 h-14 rounded-xl bg-white flex items-center justify-center mx-auto mb-6 shadow-sm">
                  <item.icon className="w-6 h-6 text-[#BFA05F]" />
                </div>
                <h3 className="font-heading text-xl font-medium text-[#1A1815] mb-3">
                  {item.defaultTitle}
                </h3>
                <p className="text-[#635E54] text-sm leading-relaxed">{item.defaultDesc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Commitment Banner */}
      <section className="py-16 md:py-24 bg-[#F2F0EB]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-20 text-center animate-fade-in-up">
          <h2 className="font-heading text-3xl md:text-4xl font-normal text-[#1A1815]">
            {language === 'pt'
              ? 'O meu compromisso é consigo'
              : 'My commitment is with you'}
          </h2>
          <p className="text-[#635E54] mt-6 text-lg leading-relaxed max-w-2xl mx-auto">
            {language === 'pt'
              ? 'Cada visita ao Lou\'s Way é uma promessa de cuidado, qualidade e resultados que superam as suas expectativas.'
              : 'Every visit to Lou\'s Way is a promise of care, quality and results that exceed your expectations.'}
          </p>
        </div>
      </section>
    </div>
  );
};

export default MissionPage;
